import { cn, Progress } from '@foresightdata/web-common-components';
import { LinearProgress as MuiLinearProgress } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { MaggieFeatureFlags } from '../../util/feature-flags';

/**
 * The app's LinearProgress (FD-6113): the page loading bar and the upload bar. MUI today;
 * web-common's Progress (Base UI) with the flag on. Indeterminate unless a `value` is given;
 * a value is clamped to 0–100, since MUI and Base UI treat an out-of-range one differently.
 * No `sx`, no `buffer` / `query` variants, one colour (primary).
 */
export interface LinearProgressProps {
  /** 0–100; omit for an indeterminate bar. */
  value?: number;
  'aria-label'?: string;
  className?: string;
}

export function LinearProgress({ value: rawValue, className, ...rest }: LinearProgressProps) {
  const { useV2Components } = useFlags<MaggieFeatureFlags>();
  // NaN would reach the bar as a value; treat it as no value, i.e. indeterminate.
  const value =
    rawValue === undefined || Number.isNaN(rawValue) ? undefined : Math.min(100, Math.max(0, rawValue));

  if (!useV2Components) {
    return (
      <MuiLinearProgress
        variant={value === undefined ? 'indeterminate' : 'determinate'}
        value={value}
        className={className}
        {...rest}
      />
    );
  }

  return <Progress value={value ?? null} className={cn('w-full', className)} {...rest} />;
}
